import { useEffect, useState } from "react";
import { Loader2, TrendingUp, Users, AlertTriangle, XCircle, DollarSign, Sparkles, RefreshCw } from "lucide-react";
import { http } from "../lib/api";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function Tile({ icon: Icon, label, value, sub, tone = "default", testid }) {
  const tones = {
    default: "bg-white border-[#E5D8C1] text-[#1A1A1A]",
    good: "bg-[#7CB342]/10 border-[#7CB342] text-[#1A1A1A]",
    warn: "bg-[#FFB300]/15 border-[#FFB300] text-[#1A1A1A]",
    bad: "bg-[#D32F2F]/10 border-[#D32F2F] text-[#1A1A1A]",
    hero: "bg-[#1A1A1A] border-[#1A1A1A] text-white",
  };
  const iconColor = {
    default: "text-[#5C5A56]", good: "text-[#7CB342]", warn: "text-[#B78900]", bad: "text-[#D32F2F]", hero: "text-[#FFB300]",
  }[tone];
  return (
    <div className={`border-2 ${tones[tone]} p-5`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} className={iconColor} />
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      </div>
      <div className="font-heading text-4xl mt-2">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

export function SubscriptionHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await http.get("/admin/subscriptions/health");
      setData(data);
    } catch { /* silent — tile just won't render */ }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="border border-dashed border-[#E5D8C1] p-8 text-center text-[#5C5A56]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading subscription health…</div>;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="admin-sub-health">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-3xl uppercase tracking-wide">Chamoy Club — Health</h2>
        <button
          onClick={load}
          disabled={refreshing}
          className="text-xs uppercase tracking-widest flex items-center gap-1 border border-[#E5D8C1] px-3 py-2 hover:border-[#D32F2F] disabled:opacity-50"
          data-testid="admin-sub-health-refresh"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          icon={DollarSign}
          label="MRR"
          value={money(data.mrr)}
          sub={`${money(data.arr)} ARR`}
          tone="hero"
          testid="sub-health-mrr"
        />
        <Tile
          icon={Users}
          label="Active members"
          value={data.active_members}
          sub={`${data.new_signups_30d} new in 30d`}
          tone="good"
          testid="sub-health-active"
        />
        <Tile
          icon={AlertTriangle}
          label="Past due"
          value={data.past_due}
          sub={data.past_due > 0 ? "Needs follow-up" : "All good"}
          tone={data.past_due > 0 ? "bad" : "default"}
          testid="sub-health-past-due"
        />
        <Tile
          icon={XCircle}
          label="Cancelled (30d)"
          value={data.cancellations_30d}
          sub={`${data.churn_rate_30d}% churn · ${data.cancel_at_period_end} ending soon`}
          tone={data.cancellations_30d > 0 ? "warn" : "default"}
          testid="sub-health-cancelled"
        />
        <Tile
          icon={TrendingUp}
          label="Lifetime revenue"
          value={money(data.lifetime_revenue)}
          sub="from paid invoices"
          testid="sub-health-revenue"
        />
        <Tile
          icon={Sparkles}
          label="Total ever"
          value={data.total_ever}
          sub="all-time signups"
          testid="sub-health-total"
        />
        <Tile
          icon={DollarSign}
          label="Price / month"
          value={money(data.monthly_price)}
          sub="per member"
          testid="sub-health-price"
        />
        <Tile
          icon={TrendingUp}
          label="Retention"
          value={`${Math.max(0, 100 - data.churn_rate_30d).toFixed(1)}%`}
          sub="30-day"
          tone="good"
          testid="sub-health-retention"
        />
      </div>
    </div>
  );
}
